import { Stack, Typography } from "@mui/joy";
import { ScoreDateType, TestResultType } from "api/dashboardApi";
import { selfCheckData, SelfCheckIdType } from "data/selfCheckData";
import SelfCheckLineChart from "components/dashboard/SelfCheckLineChart";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { useMemo } from "react";
import { theme } from "styles/theme";

function getMaxScoreById(id: SelfCheckIdType) {
  const resultRangeData = selfCheckData[id].result;
  return resultRangeData[resultRangeData.length - 1].range[1];
}

function TestResultById({
  id,
  title,
  scoreList,
}: {
  id: SelfCheckIdType;
  title: string;
  scoreList: ScoreDateType[];
}) {
  const user = useRecoilValue(userAtom);

  const xLabels = useMemo(() => {
    const start = user?.programStartedAt
      ? new Date(user.programStartedAt)
      : undefined;
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const isValidDate = (d: Date | undefined) => !!d && !isNaN(d.getTime());

    if (!isValidDate(start)) {
      return scoreList.map((_, idx) => `W${idx + 1}`);
    }

    return scoreList.map((e, idx) => {
      const d = new Date(e.date as any);
      if (!isValidDate(d)) {
        return `W${idx + 1}`;
      }
      const diffMs = d.getTime() - (start as Date).getTime();
      const diffDays = Math.floor(diffMs / MS_PER_DAY);
      const week = Math.floor(diffDays / 7) + 1;
      return `W${week < 1 ? 1 : week}`;
    });
  }, [scoreList, user?.programStartedAt]);

  return (
    <Stack
      direction="column"
      flexGrow={1}
      sx={{
        p: "20px",
        borderRadius: "12px",
        backgroundColor: "white",
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems={"center"}
        >
          <Stack direction={"row"} spacing="10px" alignItems={"center"}>
            <Typography
              variant={"Subtitle"}
              sx={{ fontWeight: 700, color: theme.vars.palette.Content.Strong }}
            >
              {title}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      {scoreList.length > 0 && (
        <SelfCheckLineChart
          id={id}
          data={scoreList.map((e, index) => e.score)}
          maxScore={getMaxScoreById(id)}
          xLabel={xLabels}
        />
      )}
    </Stack>
  );
}

export default function TestResult({
  anxiety,
  depression,
  stress,
}: TestResultType) {
  const data: { id: SelfCheckIdType; scoreList: ScoreDateType[] }[] = [
    {
      id: "depression",
      scoreList: depression?.scoreList || [],
    },
    {
      id: "anxiety",
      scoreList: anxiety?.scoreList || [],
    },
    {
      id: "stress" as SelfCheckIdType,
      scoreList: stress?.scoreList || [],
    },
  ];

  const allEmpty = data.every(({ scoreList }) => (scoreList?.length ?? 0) === 0);
  if (allEmpty) {
    return (
      <Stack
        direction={"column"}
        alignItems="center"
        justifyContent="center"
        sx={{ width: "100%", minHeight: "70vh" }}
      >
        <Typography
          variant={"Body"}
          sx={{
            fontWeight: 500,
            color: theme.vars.palette.Content.Medium,
            textAlign: "center",
            whiteSpace: "pre-line",
          }}
        >
          {"아직 측정하지 않았어요.\n" + "코치의 안내에 따라 활동을 진행해주세요."}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction={"column"} spacing={1}>
      {data.map(({ id, scoreList }, index) => {
        const title = selfCheckData[id as SelfCheckIdType].title;
        return (
          <TestResultById
            key={`testResult_${id}`}
            id={id}
            title={title}
            scoreList={scoreList}
          />
        );
      })}
    </Stack>
  );
}
